import type { ProposedTodo } from "../hooks/useVoiceToTodo";
import type { GmailThread } from "./gmail";
import type { NewSubtaskFields, NewTask } from "../types";

/**
 * Maps an AI-proposed todo onto the shape `addTask` expects. Shared by the
 * voice review dialog and Captures so the two stay in sync when a task
 * field is added or renamed. Kept in its own module (not the voice hook) so it
 * can be reused without pulling in — or being mocked alongside — the hook.
 */
export function proposedTodoToNewTask(
  todo: ProposedTodo
): Omit<NewTask, "links" | "pending_files"> {
  return {
    title: todo.title,
    list_id: todo.list_id,
    priority: todo.priority,
    description: todo.description,
    scheduled_date: todo.scheduled_date,
    scheduled_time: todo.scheduled_time,
    deadline_date: todo.deadline_date,
    duration: null,
    subtasks: todo.subtasks,
    tag_ids: todo.tag_ids,
  };
}

/**
 * Maps a proposed todo onto the extra columns `useCreateSubtask` can write
 * besides title. List and nested sub-tasks stay on the draft card (shared
 * editor) but are not sub-task columns, so they are dropped here.
 */
export function proposedTodoToSubtaskFields(
  todo: ProposedTodo,
): NewSubtaskFields {
  return {
    description: todo.description,
    priority: todo.priority,
    scheduled_date: todo.scheduled_date,
    scheduled_time: todo.scheduled_time,
    deadline_date: todo.deadline_date,
  };
}

/**
 * Seed a draft todo from an email conversation, matching "Save as a task":
 * title is the subject, description is empty, priority 3. The Gmail link is
 * attached after save, not stored on the draft.
 */
export function emailThreadToProposedTodo(thread: GmailThread): ProposedTodo {
  return {
    title: thread.subject || "(no subject)",
    list_id: null,
    priority: 3,
    description: "",
    scheduled_date: null,
    scheduled_time: null,
    deadline_date: null,
    subtasks: [],
    tag_ids: [],
    confidence: 1,
  };
}
